/* Is the viewport narrower than a given width?
 *
 * A media query rather than a width kept in state, so dragging a window edge
 * re-renders once — when the breakpoint is crossed — instead of on every pixel.
 *
 * The width the caller passes is computed, never a device guess. The board asks
 * "is there room for every stage at the card's readable width", and how many
 * stages there are is the shop's to decide, so the breakpoint moves with the
 * stage list rather than with a table of iPad sizes.
 *
 * jsdom has no `matchMedia`, so the smoke test falls back to the window width.
 */
import { useCallback, useMemo, useSyncExternalStore } from 'react';

export function useNarrowerThan(px: number): boolean {
  const query = `(max-width: ${px - 1}px)`;

  const mql = useMemo(
    () => (typeof window.matchMedia === 'function' ? window.matchMedia(query) : null),
    [query],
  );

  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!mql) {
        window.addEventListener('resize', onChange);
        return () => window.removeEventListener('resize', onChange);
      }
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    },
    [mql],
  );

  const read = useCallback(() => (mql ? mql.matches : window.innerWidth < px), [mql, px]);

  return useSyncExternalStore(subscribe, read);
}

/* Below this the chrome is drawn tight, and the pages come in with it.
 *
 * It is the width the full-size header measures — the brand, the six
 * destinations, the role and the avatar, at their own padding — plus a little
 * slack. Not a device: an iPad in portrait is 820 CSS px and a small laptop is
 * narrower than a stage list, and both used to be answered by a 1024px floor
 * that simply cut the right-hand side of every screen off. The header gives up
 * its own padding instead, which costs nothing anybody reads.
 *
 * It moved out of `Shell` when Dashboard joined the row, because it is a
 * measurement of that row and not a number anybody picked. A destination added
 * there without moving it is a header that quietly stops fitting on the machine
 * it was measured for. It lives here rather than there because the pages under
 * the header ask it too. */
export const CHROME_TIGHT = 1080;

/* The gutter down each side of the app — the header's padding and every page's
   — and what it comes in to below the artboard.

   The tight left is 8px rather than a tablet's usual 16 because the shop asked
   for it three times: on an iPad the panels carry their own padding and every
   pixel between them and the glass is a day off the schedule or a column off
   the board. It is not zero — a card standing on the edge of the glass reads
   as cut off — but it is as close to it as the app goes.

   The tight right is 25px, and that number is not arithmetic. On the shop's
   iPad Air (1180 wide) the right-hand side read about 17px short of the left —
   the account avatar cut off the edge of the header, the last board column and
   the schedule's controls standing on the glass — and 25 is what the shop set
   in the browser to make the two sides read the same. Rendered here, the same
   build at the same size (desktop, emulated iPad, real fonts) lays out exactly
   symmetric, so the cause was not found; this is a correction measured on the
   device. If the right-hand side ever reads too wide, look for what is eating
   those 17px before changing this. */
export const PAGE_GUTTER = 24;
export const PAGE_GUTTER_TIGHT = 8;
export const PAGE_GUTTER_TIGHT_END = 25;

/* Where the app stops being drawn at the width it was designed at.
 *
 * 1440 is the design canvas — the artboard every screen was drawn on, and the
 * only width at which nothing in the app is giving anything up. Below it
 * something always is: the board is at a smaller card, the jobs table is
 * scrolling sideways inside itself, the schedule is showing fewer days. The
 * side padding is the cheapest of those to give and the first that should go —
 * 8px down each side is a column of a table or a slice of a card, and nobody
 * reads the air.
 *
 * It is deliberately wider than `CHROME_TIGHT`. That one is arithmetic about
 * the header's own row — the width at which the brand has to drop the city —
 * and every iPad in landscape sits above it: 1180 on an Air, 1194 on an 11-inch
 * Pro, 1366 on a 12.9. So a header that fitted was leaving every page at its
 * full 24px on exactly the device the shop works on. The gutter is not a
 * measurement of anything fitting; it is how much air the app is drawn with. */
export const ARTBOARD = 1440;

/** The two sides of the gutter, left and right. */
export type Gutter = { start: number; end: number };

/** The side gutter the app draws itself with — the header's padding, and the
 *  one every screen's `<section>` supplies.
 *
 *  One hook for both, so a title always lines up with the brand above it and a
 *  table's edge with the account menu's. Below the artboard the two come in
 *  together; a page that tightened on its own would sit further in than the
 *  header over it, which is the space the shop was seeing down the left of an
 *  iPad.
 *
 *  The board is the one screen that does not use it on the section: its
 *  columns are flush to the edge and pad themselves, by this same gutter at fit
 *  and snug. Its title band wraps itself in it. */
export function usePageGutter(): Gutter {
  return useNarrowerThan(ARTBOARD)
    ? { start: PAGE_GUTTER_TIGHT, end: PAGE_GUTTER_TIGHT_END }
    : { start: PAGE_GUTTER, end: PAGE_GUTTER };
}

/** A shorthand padding with the gutter as its sides: `withGutter(g, 0, 20)` is
 *  `0 25px 20px 8px` on a tablet. */
export function withGutter(g: Gutter, top: number, bottom = top): string {
  return `${top}px ${g.end}px ${bottom}px ${g.start}px`;
}
